import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { clsx } from 'clsx';
import { FunctionComponent, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Button } from 'softcat:components/button.tsx';
import { ErrorMessage } from 'softcat:components/error-message.tsx';

type ConfirmationModalProperties = {
    confirmButtonText: string;
    confirmErrorMessage: string;
    content: string;
    handleClose: () => void;
    handleConfirm: () => Promise<void>;
    header: string;
    isVisible: boolean;
    style?: 'confirm' | 'submit';
};

export const ConfirmationModal: FunctionComponent<ConfirmationModalProperties> = ({
    confirmButtonText,
    confirmErrorMessage,
    content,
    handleClose,
    handleConfirm,
    header,
    isVisible,
    style = 'confirm',
}) => {
    const [confirming, setConfirming] = useState(false);

    return (
        <Dialog className="relative z-[60]" data-testid={'confirmation-modal'} onClose={handleClose} open={isVisible}>
            <DialogBackdrop
                className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
                transition
            />

            <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                <div className="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0">
                    <DialogPanel
                        className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-lg data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
                        transition
                    >
                        <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                            <div className="sm:flex sm:items-start">
                                <div
                                    className={clsx('mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full sm:mx-0 sm:h-10 sm:w-10', {
                                        'bg-indigo-100': style === 'submit',
                                        'bg-red-100': style === 'confirm',
                                    })}
                                >
                                    <ExclamationTriangleIcon
                                        aria-hidden="true"
                                        className={clsx('h-6 w-6', {
                                            'text-indigo-600': style === 'submit',
                                            'text-red-600': style === 'confirm',
                                        })}
                                    />
                                </div>
                                <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                                    <DialogTitle as={'div'}>
                                        <h1 className="text-base font-semibold leading-7 text-gray-900">{header}</h1>
                                        <p className="mt-1 text-sm leading-6 text-gray-500">{content}</p>
                                    </DialogTitle>
                                </div>
                            </div>
                        </div>
                        <div className="flex flex-col place-items-end items-center gap-3 bg-gray-50 p-4 sm:mt-6 sm:flex-row-reverse sm:rounded-lg sm:p-3">
                            <Button
                                onClick={async () => {
                                    setConfirming(true);

                                    await handleConfirm().finally(() => {
                                        setConfirming(false);
                                    });
                                }}
                                style={style}
                                thinking={confirming}
                            >
                                {confirmButtonText}
                            </Button>
                            <Button
                                focus={true}
                                onClick={() => {
                                    return handleClose();
                                }}
                                style={'cancel'}
                            >
                                <FormattedMessage id="BUTTON_CANCEL" />
                            </Button>
                            <ErrorMessage>{confirmErrorMessage}</ErrorMessage>
                        </div>
                    </DialogPanel>
                </div>
            </div>
        </Dialog>
    );
};
